import type { SupabaseClient, User } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { fetchTermsAccepted } from "./acceptance";
import { TERMS_VERSION } from "./version";

export type TermsAcceptanceStatus = "checking" | "accepted" | "required";

interface UseTermsAcceptanceResult {
  status: TermsAcceptanceStatus;
  /** À appeler après un `recordTermsAcceptance` réussi (évite un aller-retour réseau). */
  markAccepted: () => void;
}

// Pas de polling : le cache module (acceptations confirmées seulement) évite
// le réseau aux comptes déjà acceptés ; un compte non accepté re-vérifie à
// chaque montage de ProtectedRoute, sans conséquence (le gate est déjà
// affiché). `user` null (pas encore authentifié) laisse le statut "checking" ;
// ProtectedRoute ne le consulte de toute façon que pour le cas authenticated.
export function useTermsAcceptance(
  client: SupabaseClient,
  user: User | null,
): UseTermsAcceptanceResult {
  const [status, setStatus] = useState<TermsAcceptanceStatus>("checking");

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setStatus("checking");

    fetchTermsAccepted(client, user.id, TERMS_VERSION).then(
      (result) => {
        if (!alive) return;
        if (!result.ok) {
          // Fail-open : le but est la PREUVE d'acceptation, pas un mur — une
          // erreur de lecture (réseau, RLS transitoire) ne doit jamais bloquer
          // l'app indéfiniment (patron isLlmSuspended, spec §cas limites).
          console.warn("useTermsAcceptance: lecture échouée, fail-open", result.error);
          setStatus("accepted");
          return;
        }
        setStatus(result.accepted ? "accepted" : "required");
      },
      (err: unknown) => {
        if (!alive) return;
        console.warn("useTermsAcceptance: exception inattendue, fail-open", err);
        setStatus("accepted");
      },
    );

    return () => {
      alive = false;
    };
  }, [client, user]);

  const markAccepted = useCallback(() => setStatus("accepted"), []);

  return { status, markAccepted };
}
